import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { AuthService } from '@auth0/auth0-angular';
import { MenuItem, TreeNode } from 'primeng/api';
import { merge, Observable, of } from 'rxjs';
import { catchError, filter, skipWhile, take, timeout } from 'rxjs/operators';

import { MainFacade } from '../../main.facade';

import { FileProject } from '../../models/file-info';

@Component({
  selector: 'main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MainComponent implements OnInit {
  public projects!: FileProject[];

  public activeItemL$!: Observable<MenuItem | null>; // left panel active item
  public activeItemR$!: Observable<MenuItem | null>; // right panel active item
  public itemsTabMenu: MenuItem[] = [];

  detailsDisabled = true;

  // Set once the Results tab has been opened for the first time, then left
  // true - the template uses it to keep <results-tab> mounted (via [hidden])
  // rather than destroying/recreating it on every tab switch, so its search
  // results persist and don't get silently re-fetched on each visit.
  resultsTabActivated = false;

  // Shown while a deep-linked resultPath (?resultPath=gs://...) is being
  // resolved — bucket lookup, tree walk, and file open all run in the
  // background with no other feedback until the file appears.
  deepLinkLoading = false;

  user$ = this.auth.user$;

  constructor(public auth: AuthService, public mainFacade: MainFacade, private route: ActivatedRoute) {}

  ngOnInit(): void {
    // TO-DO: this can be moved to utils or config file
    this.itemsTabMenu = [
      {
        id: 'files',
        label: 'Files',
        icon: 'pi pi-folder',
        command: () => {
          this.mainFacade.selectTabMenuItemL$(this.itemsTabMenu[0]);
        },
      },
      {
        id: 'search',
        label: 'Search',
        icon: 'pi pi-search',
        command: () => {
          this.mainFacade.selectTabMenuItemL$(this.itemsTabMenu[1]);
        },
      },
      {
        id: 'results',
        label: 'Results',
        icon: 'pi pi-table',
        command: () => {
          this.mainFacade.selectTabMenuItemL$(this.itemsTabMenu[2]);
        },
      },
      {
        id: 'details',
        label: 'Details',
        icon: 'pi pi-info-circle',
        command: () => {
          this.mainFacade.selectTabMenuItemL$(this.itemsTabMenu[3]);
        },
        disabled: this.detailsDisabled,
      },
      {
        id: 'process',
        label: 'Process',
        icon: 'pi pi-cog',
        command: () => {
          this.mainFacade.selectTabMenuItemR$(this.itemsTabMenu[4]);
        },
      },
      {
        id: 'queue',
        label: 'Queue',
        icon: 'pi pi-clock',
        command: () => {
          this.mainFacade.selectTabMenuItemR$(this.itemsTabMenu[5]);
        },
      },
      {
        id: 'rules',
        label: 'Rules',
        icon: 'pi pi-bell',
        command: () => {
          this.mainFacade.selectTabMenuItemR$(this.itemsTabMenu[6]);
        },
      },
      // The Regions editor moved from a right-panel tab to a floating dialog
      // opened from the plotting toolbar (see VisualizerComponent).
    ];
    // create tab-menu observable
    this.mainFacade.createMainTabMenu(this.itemsTabMenu);
    // set selected tab-menu items (left and right panels)
    this.mainFacade.selectTabMenuItemL$(this.itemsTabMenu[0]);
    this.mainFacade.selectTabMenuItemR$(this.itemsTabMenu[4]);
    // get observable references - this way other components can update the
    // selected tab-menu item and this will update the template
    this.activeItemL$ = this.mainFacade.getSelectedTabMenuItemL$();
    this.activeItemR$ = this.mainFacade.getSelectedTabMenuItemR$();

    this.activeItemL$.subscribe((item) => {
      if (item?.id === 'results') this.resultsTabActivated = true;
    });

    this.mainFacade.getProjects$().subscribe({
      next: (projects) => {
        this.projects = projects;
      },
    });

    this.auth.isAuthenticated$.subscribe({
      next: (isAuthenticated) => {
        if (isAuthenticated) {
          this.mainFacade.loadProjects();
        }
      },
      error: (e) => {
        // TODO [gik 01/03/2023] show user friendly message on the page
        // why the authentication attempt was not successful.
        console.log(e);
      },
    });

    // supports opening a direct link to a result, e.g. from pyjit:
    // https://<host>/main?resultPath=gs://<bucket>/<path>
    const path = this.route.snapshot.queryParamMap.get('path');
    if (path) {
      this.deepLinkLoading = true;
      this.mainFacade.getProjects$().pipe(
        filter((projects) => projects.length > 0),
        take(1)
      ).subscribe(() => {
        this.mainFacade.openResultPath(path);
      });

      // openResultPath() fans out into nested subscriptions (bucket lookup,
      // project select, tree walk, node select, image load) and returns
      // immediately, so there's no single "done" callback to await. Instead,
      // wait for the file/image loading flags it ultimately drives to go
      // true and then back to false. skipWhile guards against a flag that's
      // already false before loading has actually started; the timeout stops
      // the spinner from spinning forever if the path/bucket never resolves
      // to anything (e.g. a bad deep link).
      merge(this.mainFacade.isImageLoading$(), this.mainFacade.isFileLoading$()).pipe(
        skipWhile((loading) => !loading),
        filter((loading) => !loading),
        take(1),
        timeout(30000),
        catchError(() => of(null))
      ).subscribe(() => {
        this.deepLinkLoading = false;
      });
    }

    this.mainFacade.isSelectedFileFolder$().subscribe((isFolder) => {
      // Details is now index 3 (after Files/Search/Results) - see the
      // itemsTabMenu array above.
      if (isFolder || isFolder == null) {
        this.itemsTabMenu[3].disabled = true;
        this.mainFacade.createMainTabMenu(this.itemsTabMenu);
      } else {
        this.itemsTabMenu[3].disabled = false;
        this.mainFacade.createMainTabMenu(this.itemsTabMenu);
      }
    });
  }

  onProjectSelect(event: any): void {
    this.mainFacade.selectProject(event.value);
  }

  interuptPreview(): void {
    this.mainFacade.interuptPreview();
  }

  processSelected(): void {
    this.mainFacade.processSelected();
  }

  nodeExpand(node: TreeNode) {
    this.mainFacade.updateNode(node);
  }

  nodeSelect(node: TreeNode) {
    this.mainFacade.nodeSelect(node);
  }

  loadingDropdown() {
    if (this.projects.length <= 0) {
      return 'pi pi-spin pi-spinner';
    } else {
      return '';
    }
  }

  splitDragEvent(event: any) {
    this.mainFacade.setPanelWidth(event.sizes[1]);
  }
}
